import React, { useState } from 'react';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, CustomSelect } from 'src/components';
import { toastAlert } from 'src/utils/toastAlert';
import Service from 'src/setup/network';
import WorkingDays from 'src/components/WorkingDays';
import { fetchDetails } from 'src/redux/action/details.action';
import { useDispatch } from 'react-redux';
import TextField from 'src/components/text-field';
import { DefaultSelect } from 'src/components/defaultSelect';
import { dateOption } from 'src/utils/options';
import { AiOutlineClockCircle } from 'react-icons/ai';
import Loader from 'src/components/loader';

interface IProps {
  workingHours?: any;
  loading?: boolean;
}

const FORM_VALIDATION = Yup.object().shape({
  startTime: Yup.date(),
  endTime: Yup.date().min(
    Yup.ref('startTime'),
    'End Time cannot be before start Time'
  ),
});

const WorkingHours: React.FC<IProps> = ({ workingHours, loading }) => {
  const daysOfWeek = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];
  const dispatch = useDispatch();

  const [isChecked, setIsChecked] = useState<any>({
    sunday: Boolean(workingHours?.sunday?.isOpen),
    monday: Boolean(workingHours?.monday?.isOpen),
    tuesday: Boolean(workingHours?.tuesday?.isOpen),
    wednesday: Boolean(workingHours?.wednesday?.isOpen),
    thursday: Boolean(workingHours?.thursday?.isOpen),
    friday: Boolean(workingHours?.friday?.isOpen),
    saturday: Boolean(workingHours?.saturday?.isOpen),
  });

  const [dayValues, setDayValues] = useState<any>({
    sunday: {
      from: workingHours?.sunday?.startTime || '',
      to: workingHours?.sunday?.endTime || '',
    },
    monday: {
      from: workingHours?.monday?.startTime || '',
      to: workingHours?.monday?.endTime || '',
    },
    tuesday: {
      from: workingHours?.tuesday?.startTime || '',
      to: workingHours?.tuesday?.endTime || '',
    },
    wednesday: {
      from: workingHours?.wednesday?.startTime || '',
      to: workingHours?.wednesday?.endTime || '',
    },
    thursday: {
      from: workingHours?.thursday?.startTime || '',
      to: workingHours?.thursday?.endTime || '',
    },
    friday: {
      from: workingHours?.friday?.startTime || '',
      to: workingHours?.friday?.endTime || '',
    },
    saturday: {
      from: workingHours?.saturday?.startTime || '',
      to: workingHours?.saturday?.endTime || '',
    },
  });

  const initValues = {
    isChecked,
    ...dayValues,
  };

  const handleDayChange = (day: string) => {
    setIsChecked((prevChecked: any) => ({
      ...prevChecked,
      [day]: !prevChecked[day],
    }));
  };

  const handleSubmit = async (val: any) => {
    const workingHours: Record<string, any> = {};
    daysOfWeek.forEach((day) => {
      workingHours[day] = {
        isOpen: isChecked[day],
        startTime: val[day].from,
        endTime: val[day]?.to,
      };
    });
    const payload = { workingHours };
    try {
      await Service.post('/settings/update', payload);
      toastAlert('success', `Working Hours is successfully updated`);
      dispatch(fetchDetails() as any);
    } catch (err: any) {
      toastAlert('error', err?.response?.data?.message);
    }
  };
  const handleCopy = () => {
    const copiedValues = { ...dayValues };
    const sundayValues = dayValues.sunday;
    daysOfWeek.forEach((day) => {
      if (day !== 'sunday') {
        copiedValues[day] = {
          from: sundayValues.from,
          to: sundayValues.to,
        };
      }
    });
    setDayValues(copiedValues);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={initValues}
          validateOnMount
          onSubmit={handleSubmit}
          validationSchema={FORM_VALIDATION}
        >
          {({ isSubmitting, isValid, values, setFieldValue }) => (
            <Form>
              {/* <p
          className="flex items-center justify-end mb-4 ml-auto cursor-pointer bg-primary text-[white] p-2 rounded-xl w-fit"
          onClick={handleCopy}
        >
          Copy to All
        </p> */}

              {daysOfWeek.map((day) => (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="font-bold capitalize">{day}</p>
                    <div className="flex">
                      <p className="mr-2 font-semibold">Closed</p>
                      <label className="toggle inline-block w-12 h-6 bg-gray-100 rounded-full border-2 border-light-gray relative cursor-pointer">
                        <div
                          className={`absolute w-6 h-6 rounded-[50%] -top-[2px] -left-[2px] bg-light-gray transition-transform duration-500 ${
                            isChecked[day]
                              ? 'transform translate-x-full bg-primary'
                              : ''
                          }`}
                        ></div>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={isChecked[day]}
                          onChange={() => handleDayChange(day)}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <div className="relative">
                      <Field
                        name={`${day}.from`}
                        className="mt-3 w-[158px]"
                        options={dateOption ?? []}
                        component={CustomSelect}
                        defaultValue={
                          values[day]?.from ?? dayValues[day]?.from ?? ''
                        }
                        isMulti={false}
                        label="From"
                        placeholder={''}
                      />
                      {/* <div className="absolute top-11 right-4 cursor-pointer"> */}
                      {/* <AiOutlineClockCircle size={24} /> */}
                      {/* </div> */}
                    </div>
                    <Field
                      name={`${day}.to`}
                      className="mt-3 w-[158px]"
                      options={dateOption ?? []}
                      component={CustomSelect}
                      defaultValue={values[day]?.to ?? dayValues[day]?.to ?? ''}
                      label="To"
                      placeholder={''}
                    />
                  </div>
                </>
              ))}

              <div className="flex items-center justify-end my-2">
                <Button
                  text="Save Details"
                  variant="primary"
                  type="submit"
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default WorkingHours;
